import 'web-animations-js';
import { oneLine } from 'common-tags';
import template from 'lodash-es/template';
import debounce from 'lodash-es/debounce';
import { T_ } from '@shared/translation';
import jQuery from 'jquery';
import 'jquery-validate';
import { usernameSuggester } from '@shared/username-suggester';
import uiPaneSliderFactory from '@shared/ui-pane-slider';
import { custom_validation_rules } from './custom-validation-methods';
import templateForm from './template-form.html';

custom_validation_rules.validateEmail();
custom_validation_rules.validate_username();
custom_validation_rules.different_from();
custom_validation_rules.validate_password_strength();

/**
 * Generates a unique identifier
 * @return {string}  String for the unique identifier.
 */
function generateUniqueIdentifier() {
  const identifier = `passwordless-${Math.floor(Math.random() * 1024)}`;
  // Does the passwordless form with that identifier exist? Reroll the
  // dice again to get a new unique one.
  if (document.getElementById(identifier)) {
    return generateUniqueIdentifier();
  }

  return identifier;
}

export default class PasswordlessForm {
  constructor(options = {}) {
    this.identifier = generateUniqueIdentifier();
    this.submissionCallback = options.submissionCallback || (() => {});
    this.onSignupModeActivated =
      options.onSignupModeActivated || (() => {});
    this.onSignupModeDeactivated =
      options.onSignupModeDeactivated || (() => {});
    this.extraValidations = options.extraValidations || {};
    this.afterPaneSwitchEventHandler = options.afterPaneSwitchEventHandler ||
      (() => {});

    // Set a toggle to check if the input has validated and parts of the
    // login/signup flow has shown.
    this.emailValidatedOnce = false;

    return this;
  }

  _checkEmailAvailable(email) {
    return new Promise((resolve) => {
      fetch(oneLine`/ajax/check-email-exist.php?json=true&validator=true
        &email=${encodeURIComponent(email)}`)
        .then(response => response.json())
        .then(resolve);
    });
  }

  _fetchInitialCategories() {
    return fetch('/ajax/skill-bundle.php?category')
      .then(response => response.json());
  }

  fetchHTML() {
    return new Promise((resolve, reject) => {
      this._fetchInitialCategories()
        .then((categoryData) => {
          resolve(template(templateForm)({
            identifier: this.identifier,
            options: categoryData,
            T_,
            hasCategory: false,
          }));
        })
        .catch(reject);
    });
  }

  /**
   * Resets the form and switches back to the first pane.
   */
  reset() {
    this.formEl.reset();
    this.uiPaneSlider.switchPanes(1);
  }

  // TODO: Temporary API plug while we deprecate PasswordlessForm
  switchPanes(...args) {
    this.uiPaneSlider.switchPanes(...args);
  }

  // TODO: Temporary API plug while we deprecate PasswordlessForm
  setFirstFocus(...args) {
    this.uiPaneSlider.setFirstFocus(...args);
  }

  _enableUsernameField() {
    this.usernameContainerEl.classList.remove('is-hidden');
    this.usernameEl.removeAttribute('disabled');
    this.usernameEl.setAttribute('data-first-focus', true);
  }

  _disableUsernameField() {
    this.usernameContainerEl.classList.add('is-hidden');
    this.usernameEl.setAttribute('disabled', 'disabled');
    this.usernameEl.removeAttribute('data-first-focus');
    this.usernameEl.value = '';
  }

  _enablePasswordField() {
    this.passwordContainerEl.classList.remove('is-hidden');
    this.passwordEl.removeAttribute('disabled');
    this.passwordEl.setAttribute('data-first-focus', true);
  }

  _disablePasswordField() {
    this.passwordContainerEl.classList.add('is-hidden');
    this.passwordEl.setAttribute('disabled', 'disabled');
    this.passwordEl.removeAttribute('data-first-focus');
    this.passwordEl.value = '';
  }

  /*
    Toggle Password Validation
    @params on - boolean
  */
  togglePasswordFieldValidation(on) {
    if (on) {
      jQuery(this.passwordEl).rules('remove');
      jQuery(this.passwordEl).rules('add', { required: true });
    } else {
      jQuery(this.passwordEl).rules('remove');
      jQuery(this.passwordEl).rules('add', this.passwordRules || {});
    }
  }

  activateSignupMode() {
    this._enableUsernameField();
    this._enablePasswordField();

    this.onSignupModeActivated();
  }

  activateLoginMode() {
    this._enablePasswordField();
    this.togglePasswordFieldValidation(true);
  }

  deactivateSignupMode() {
    this._disableUsernameField();
    this.onSignupModeDeactivated();
  }

  deactivateLoginMode() {
    this._disablePasswordField();
    this.togglePasswordFieldValidation(false);
  }

  isLoginMode() {
    return this.usernameEl.getAttribute('disabled') === 'disabled';
  }

  showLoginError() {
    this.passwordContainerEl.classList.remove('success');
    this.passwordContainerEl.classList.remove('is-valid');
    this.passwordContainerEl.classList.add('is-invalid');
    this.passwordContainerEl.classList.add('error');
    this.passwordContainerEl.querySelector('.error')
      .textContent = T_('Oops! The login details you entered were incorrect.');
  }
  onEmailInputFocusOut() {
    this.emailElClean = false;
  }
  onEmailInputChange() {
    // For the first time a.k.a emailElClean = true,
    // if @ and . sign is not there, exit quickly
    if (this.emailElClean &&
      (this.emailEl.value.indexOf('@') === -1 ||
      this.emailEl.value.indexOf('.') === -1)) {
      return;
    }

    // Force re-validation of email, dont show password/username field if
    // invalid
    const emailValid = jQuery(this.emailEl).valid();
    if (!emailValid && !this.emailValidatedOnce) {
      this.deactivateSignupMode();
      this.deactivateLoginMode();
      return;
    }

    // Show the loader
    if (this.emailLoaderEl) {
      this.emailLoaderEl.classList.remove('is-hidden');
    }

    this._checkEmailAvailable(this.emailEl.value).then((emailAvailable) => {
      // hide the loader as soon as the async call has finished
      if (this.emailLoaderEl) {
        this.emailLoaderEl.classList.add('is-hidden');
      }

      if (emailAvailable) {
        this.deactivateLoginMode();
        this.activateSignupMode();
        if (!this.usernameEl.value) {
          usernameSuggester.init();
          usernameSuggester.suggestUsername(this.emailEl.value)
          .done((res) => {
            this.usernameEl.value = res.result.recommended_name;
          });
        }
      } else {
        this.deactivateSignupMode();
        this.activateLoginMode();
      }
    });
  }

  initForm(newIdentifier = this.identifier) {
    this.identifier = newIdentifier;
    this.formEl = document.getElementById(`${this.identifier}-form`);
    this.emailEl = document.getElementById(`${this.identifier}-email`);
    this.emailLoaderEl = document
      .getElementById(`${this.identifier}-email-loader`);
    this.usernameContainerEl = document
      .getElementById(`${this.identifier}-username-container`);
    this.passwordContainerEl = document
      .getElementById(`${this.identifier}-password-container`);
    this.usernameEl = document.getElementById(`${this.identifier}-username`);
    this.passwordEl = document.getElementById(`${this.identifier}-password`);
    this.prevEl = document.getElementById(`${this.identifier}-prev`);
    this.nextEl = document.getElementById(`${this.identifier}-next`);
    this.submitEl = document.getElementById(`${this.identifier}-submit`);

    this.emailElClean = true; // Clean - dirty state for email input validation

    // Initialise the pane slider UI component
    this.uiPaneSlider = uiPaneSliderFactory(newIdentifier, {
      beforePaneSwitchEventHandler: (currentPane, newPane, panes) => {
        // If the user is going back to the previous pane allow them.
        if (currentPane > newPane) {
          return true;
        }

        // Validate their choices before going to the next pane.
        const validator = this.validator;
        const allFieldsOk = Array
          .from(panes[currentPane - 1].querySelectorAll('input'))
          .filter(field => field.type !== 'radio')
          .reduce(
            (prevStatus, field) => validator.element(field) && prevStatus,
            true,
          );
        if (!allFieldsOk) {
          return false;
        }

        return true;
      },
      afterPaneSwitchEventHandler: (newPane, oldPane, panes) => {
        // If this is the first pane then hide the previous button.
        // Also check if user wants to remove back button from the current slide
        if (newPane === 1 ||
          panes[newPane - 1].hasAttribute('data-pane-backnav-disabled')) {
          this.prevEl.classList.add('is-invisible');
        } else {
          this.prevEl.classList.remove('is-invisible');
        }

        // Show the submit button if this is the last pane.
        if (newPane === panes.length) {
          this.nextEl.classList.add('is-hidden');
          this.submitEl.classList.remove('is-hidden');
        } else {
          this.nextEl.classList.remove('is-hidden');
          this.submitEl.classList.add('is-hidden');
        }

        this.afterPaneSwitchEventHandler(newPane, oldPane, panes);
      },
    });

    // TODO: Temporary API plug while we deprecate PasswordlessForm
    this.panes = this.uiPaneSlider.getPanes();
    this.nextPane = this.uiPaneSlider.nextPane;

    // Add event bindings for the previous/next nav buttons.
    this.prevEl.addEventListener('click', () => this.uiPaneSlider.prevPane());
    this.nextEl.addEventListener('click', () => this.uiPaneSlider.nextPane());

    this.passwordRules = {
      required: true,
      minlength: 6,
      validatePasswordStrength: true,
      differentFrom: () =>
        [jQuery(this.usernameEl).val(), jQuery(this.emailEl).val()],
    };

    this.validator = jQuery(`#${this.identifier}-form`).validate({
      rules: Object.assign({
        email: {
          maxlength: 100,
          required: true,
          validateEmailAddress: true,
        },
        newusername: {
          minlength: 3,
          maxlength: 16,
          validateUsername: true,
          required: true,
          remote: {
            dataType: 'json',
            url: '/ajax/check-username-exist.php',
            data: {
              json: true,
              validator: true,
              username: () => this.usernameEl.value,
            },
          },
        },
        newuserpasswd: this.passwordRules,
      }, this.extraValidations.rules),
      messages: Object.assign({
        email: {
          required: T_('Please enter an email address.'),
          email: T_('Please enter a valid email address.'),
        },
        newusername: {
          required: T_('Please enter a username.'),
          minlength: T_('Username must be at least 3 characters.'),
          maxlength: T_('Username must be at most 16 characters.'),
          validateUsername: T_`Invalid characters. Please enter 3-16
              alphanumeric characters [a-z 0-9] starting with a letter.`,
          remote: T_('This username is already in use, please try another.'),
        },
        newuserpasswd: {
          required: T_('Please enter a password.'),
          minlength: T_('Must be at least 6 characters'),
          differentFrom: T_('Must be different from email and username'),
          validatePasswordStrength: T_`Use a combination of numeric,
            lower and upper case characters to increase password strength.`,
        },
      }, this.extraValidations.messages),
      highlight: (element) => {
        jQuery(element)
          .closest('.form-step')
          .removeClass('success is-valid')
          .addClass('error is-invalid');
        this.submitEl.setAttribute('disabled', 'disabled');
        this.submitEl.classList.remove('processing');
      },
      errorPlacement: (error, element) => {
        element.parents('.form-step')
          .find('.form-error')
          .append(error);
      },
      success: (element) => {
        jQuery(element)
          .closest('.form-step')
          .removeClass('error is-invalid')
          .addClass('success is-valid');
        this.submitEl.removeAttribute('disabled');
      },
      submitHandler: (form) => {
        if (this.submissionCallback) {
          this.submissionCallback(form);
        } else {
          this.submitEl.setAttribute('disabled', 'disabled');
          this.submitEl.classList.add('processing');
          form.submit();
        }
      },
    });

    this.emailEl.addEventListener('input',
      debounce(() => this.onEmailInputChange(), 600),
    );

    this.emailEl.addEventListener('focusout',
      this.onEmailInputFocusOut.bind(this),
      { once: true }, // only run once
    );
  }
}
